import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import BirthdayCake from '@wix/da-shared-react/pkg/Icons/BirthdayCake';
import LocationPin from '@wix/da-shared-react/pkg/Icons/LocationPin';
import DeviantArtLogo from '@wix/da-shared-react/pkg/Icons/DeviantArtLogo';
import { dateToDistanceTrans } from '@wix/da-shared-react/pkg/Timestamp/functions';
import { MobileContext } from '@wix/da-shared-react/pkg/Context';
import Link from '@wix/da-shared-react/pkg/Icons/Link';
import { BulletItem, BulletList } from '@wix/da-shared-react/pkg/Bullets';
import { sanitizeExternalUrl } from '../utils';
import s from './UserInfoBullets.scss';

function getJoinDateFromDeviantFor(deviantFor, isMobile) {
  const joinDate = new Date();
  joinDate.setTime(joinDate.getTime() - deviantFor * 1000);
  return dateToDistanceTrans(joinDate.toISOString(), isMobile);
}

function getPronounKeyFromGender(gender): string {
  switch (gender) {
    case 'male':
      return 'widgets.aboutMe.gender.complete.he.him';
    case 'female':
      return 'widgets.aboutMe.gender.complete.she.her';
    case 'other':
      return 'widgets.aboutMe.gender.complete.they.them';
    default:
      return '';
  }
}

interface Props {
  dateOfBirth?: string;
  countryCode?: string;
  deviantFor?: number;
  gender?: string;
  website?: string;
  websiteLabel?: string;
}

const UserInfoBullets: React.FC<Props> = ({
  dateOfBirth,
  countryCode,
  deviantFor,
  gender,
  website,
  websiteLabel,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const deviantForDisplay = getJoinDateFromDeviantFor(deviantFor, isMobile);
  return (
    <BulletList className={s['icon-bullets']}>
      {dateOfBirth && (
        <BulletItem bullet={<BirthdayCake disableDefaultColors />}>
          {dateOfBirth}
        </BulletItem>
      )}
      {!!countryCode && (
        <BulletItem bullet={<LocationPin disableDefaultColors />}>
          {t!(`widgets.aboutMe.country.${countryCode || 'unknown'}`)}
        </BulletItem>
      )}
      <BulletItem bullet={<DeviantArtLogo disableDefaultColors />}>
        {t!('widgets.aboutMe.deviantFor.updated', {
          deviantFor: t(deviantForDisplay.key, {
            count: deviantForDisplay.value,
          }),
        })}
      </BulletItem>
      {website && (
        <BulletItem bullet={<Link disableDefaultColors />}>
          <a href={sanitizeExternalUrl(website)} rel="nofollow noopener">
            {websiteLabel || website}
          </a>
        </BulletItem>
      )}
      {gender && <BulletItem>{t(getPronounKeyFromGender(gender))}</BulletItem>}
    </BulletList>
  );
};
UserInfoBullets.displayName = 'UserInfoBullets';

export default UserInfoBullets;
